import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faComments, faHeadset, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { HubSpotConfig } from '@core/config/hubspot.config';

@Component({
  selector: 'app-support-chat',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  template: `
    <div 
      class="fixed z-50 transition-all duration-300 ease-in-out"
      [ngClass]="getPositionClasses()"
      [ngStyle]="customPositionStyle">
      
      <!-- Botón principal del chat -->
      <button
        (click)="toggleChat()"
        [class]="getButtonClasses()"
        [title]="buttonText">
        
        <fa-icon 
          [icon]="buttonIcon" 
          [ngClass]="getIconClasses()">
        </fa-icon>
        
        <span 
          *ngIf="showText"
          class="ml-2 font-medium">
          {{buttonText}}
        </span>
        
        <!-- Badge de notificación (opcional) -->
        <span 
          *ngIf="showNotificationBadge"
          class="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
          <span class="text-xs text-white font-bold">!</span>
        </span>
      </button>
    </div>
  `,
  styles: [`
    .support-chat-btn {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transition: all 0.3s ease;
    }
    
    .support-chat-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    }
    
    .support-chat-btn:active {
      transform: translateY(0);
    }
  `]
})
export class SupportChatComponent {
  @Input() position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'custom' = 'bottom-right';
  @Input() customPosition?: { top?: string; bottom?: string; left?: string; right?: string };
  @Input() buttonStyle: 'circular' | 'rounded' | 'pill' = 'circular';
  @Input() buttonColor: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'custom' = 'primary';
  @Input() customColor?: string;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() showText: boolean = false;
  @Input() buttonText: string = 'WhatsApp';
  @Input() iconType: 'whatsapp' | 'comments' | 'headset' | 'question' = 'whatsapp';
  @Input() showNotificationBadge: boolean = false;

  // Iconos disponibles
  faWhatsapp = faWhatsapp;
  faComments = faComments;
  faHeadset = faHeadset;
  faQuestionCircle = faQuestionCircle;

  get buttonIcon() {
    switch (this.iconType) {
      case 'comments':
        return this.faComments;
      case 'headset':
        return this.faHeadset;
      case 'question':
        return this.faQuestionCircle;
      default:
        return this.faWhatsapp;
    }
  }

  get customPositionStyle() {
    if (this.position === 'custom' && this.customPosition) {
      return this.customPosition;
    }
    return {};
  }

  getPositionClasses(): string {
    switch (this.position) {
      case 'bottom-left':
        return 'bottom-6 left-6';
      case 'top-right':
        return 'top-6 right-6';
      case 'top-left':
        return 'top-6 left-6';
      case 'custom':
        return '';
      default: // bottom-right
        return 'bottom-6 right-6';
    }
  }

  getButtonClasses(): string {
    let classes = 'support-chat-btn relative flex items-center justify-center font-semibold transition-all duration-300 cursor-pointer ';
    
    // Tamaño
    switch (this.size) {
      case 'small':
        classes += this.showText ? 'px-4 py-2 text-sm ' : 'w-12 h-12 ';
        break;
      case 'large':
        classes += this.showText ? 'px-8 py-4 text-lg ' : 'w-16 h-16 ';
        break;
      default: // medium
        classes += this.showText ? 'px-6 py-3 text-base ' : 'w-14 h-14 ';
    }

    // Forma
    switch (this.buttonStyle) {
      case 'rounded':
        classes += 'rounded-lg ';
        break;
      case 'pill':
        classes += 'rounded-full ';
        break;
      default: // circular
        classes += 'rounded-full ';
    }

    // Color
    if (this.buttonColor === 'custom' && this.customColor) {
      classes += `text-white `;
    } else {
      switch (this.buttonColor) {
        case 'secondary':
          classes += 'bg-gray-600 hover:bg-gray-700 text-white ';
          break;
        case 'success':
          classes += 'bg-green-600 hover:bg-green-700 text-white ';
          break;
        case 'info':
          classes += 'bg-blue-500 hover:bg-blue-600 text-white ';
          break;
        case 'warning':
          classes += 'bg-yellow-500 hover:bg-yellow-600 text-white ';
          break;
        case 'danger':
          classes += 'bg-red-600 hover:bg-red-700 text-white ';
          break;
        default: // primary
          classes += 'bg-[var(--color-brand-400)] hover:bg-[var(--color-brand-500)] text-white ';
      }
    }

    return classes;
  }

  getIconClasses(): string {
    switch (this.size) {
      case 'small':
        return 'text-sm';
      case 'large':
        return 'text-xl';
      default:
        return 'text-lg';
    }
  }

  toggleChat(): void {
    const whatsappUrl = HubSpotConfig.fallback.whatsapp;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  }
}
